import React, { useEffect, useMemo, useState } from 'react';
import AuditLogRow from './AuditLogRow';
import ShareWithRow from './ShareWithRow';
import ViewHistoryRow from './ViewHistoryRow';

interface IActivityTabProps {
  cur_case: any;
  handleTabSwitch: any;
  setShareModal: any;
  owner: string;
  selectedVersionIndex: number;
  setSelectedVersionIndex: (index: number) => void;
  // Version History only makes sense for ticket types that can be re-screened
  // (the same types that show the Settings tab) -- other ticket types have no
  // screening versions to list, so the card is dropped and Shared With /
  // Audit Log share the row instead.
  showVersionHistory: boolean;
}

const ActivityComponent: React.FC<IActivityTabProps> = ({
  cur_case,
  handleTabSwitch,
  setShareModal,
  owner,
  selectedVersionIndex,
  setSelectedVersionIndex,
  showVersionHistory,
}) => {
  const [currentItems, setCurrentItems] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSharedWith, setCurrentPageSharedWith] = useState(1);
  const [sharedWithUsers, setSharedWithUsers] = useState<any[]>([]);

  const verisonsArr = cur_case.data.results;
  const sharedArr = cur_case.data.sharedWith;
  const activities = cur_case.data.activity;
  const sortedActivities = useMemo(() => {
    if (!Array.isArray(activities)) {
      return [];
    }

    return [...activities].sort((a: any, b: any) => {
      const aTime = new Date(a?.timeStamp || 0).getTime();
      const bTime = new Date(b?.timeStamp || 0).getTime();
      return bTime - aTime;
    });
  }, [activities]);
  const itemsPerPage = 5;
  const usersPerPage = 4;
  const totalPages = Math.max(
    1,
    Math.ceil(sortedActivities.length / itemsPerPage),
  );
  const totalPagesSharedWith = Math.max(
    1,
    Math.ceil(cur_case.data.sharedWith?.length / usersPerPage),
  );

  const handleNextPage = () => {
    setCurrentPage((prevPage) =>
      Math.min(prevPage + 1, Math.ceil(sortedActivities.length / itemsPerPage)),
    );
  };
  const handleNextPageSharedWith = () => {
    if (sharedArr?.length > 4) {
      setCurrentPageSharedWith((prevPage) =>
        Math.min(prevPage + 1, Math.ceil(sharedArr?.length / usersPerPage)),
      );
    }
  };

  const handlePrevPage = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
  };
  const handlePrevPageSharedWith = () => {
    setCurrentPageSharedWith((prevPage) => Math.max(prevPage - 1, 1));
  };

  useEffect(() => {
    if (sortedActivities.length) {
      const indexOfLastItem = currentPage * itemsPerPage;
      const indexOfFirstItem = indexOfLastItem - itemsPerPage;
      setCurrentItems(
        sortedActivities.slice(indexOfFirstItem, indexOfLastItem),
      );
    } else {
      setCurrentItems([]);
    }
  }, [currentPage, sortedActivities]);

  useEffect(() => {
    if (sharedArr.length) {
      const indexOfLastItem = currentPageSharedWith * usersPerPage;
      const indexOfFirstItem = indexOfLastItem - usersPerPage;
      setSharedWithUsers(sharedArr.slice(indexOfFirstItem, indexOfLastItem));
    }
  }, [currentPageSharedWith, sharedArr]);

  const auditLogCard = (
    <div className='activity-cont-frame-left-table'>
      <div className='activity-cont-frame-left-table-cont'>
        <div className='activity-cont-frame-left-table-cont-text-sup'>
          <div className='activity-cont-frame-left-table-cont-text-sup-bagde'>
            <div className='activity-cont-frame-left-table-cont-text-sup-bagde-frame'>
              <div>Audit Log</div>
            </div>
          </div>
        </div>
      </div>
      <div className='audit-doc-tabe-col'>
        <div className='audit-doc-tabe-col-right-cell'>
          <div>
            <div className='audit-doc-tabe-col-left-cell-text'>User</div>
          </div>
        </div>
        <div className='audit-doc-tabe-col-right-cell'>
          <div>
            <div className='audit-doc-tabe-col-left-cell-text'>Action</div>
          </div>
        </div>
        <div className='audit-doc-tabe-col-right-cell'>
          <div>
            <div className='audit-doc-tabe-col-left-cell-text'>
              Date & Time
            </div>
          </div>
        </div>
      </div>
      <div
        className='activity-cont-frame-left-table-feed'
        style={{ paddingTop: '15px', gap: 'var(--spacing-lg, 15px)' }}
      >
        {currentItems?.map((activity: any) => (
          <>
            <AuditLogRow activity={activity} />
            <div className='activity-cont-frame-left-table-feed-row-cont-divider'></div>
          </>
        ))}
      </div>
      <div className='activity-cont-frame-pagination'>
        <div className='activity-cont-frame-pagination-text'>
          Page {currentPage} of {totalPages}
        </div>
        <div className='activity-cont-frame-pagination-action'>
          <div className='activity-cont-frame-pagination-action-prev'>
            <div
              className='activity-cont-frame-pagination-action-text'
              onClick={handlePrevPage}
            >
              Previous
            </div>
          </div>
          <div className='activity-cont-frame-pagination-action-prev'>
            <div
              className='activity-cont-frame-pagination-action-text'
              onClick={handleNextPage}
            >
              Next
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div
        className='activity-cont-frame'
        style={
          showVersionHistory
            ? undefined
            : {
                // No second row below to share the page with, so stretch
                // both cards down to the footer instead of stopping at
                // content height. 255px is the header + breadcrumb/title +
                // tab bar above this row, plus the footer below it, plus a
                // 12px gap before the footer (measured against the live
                // page, not guessed).
                minHeight: 'calc(100vh - 257px)',
                alignItems: 'stretch',
              }
        }
      >
        {showVersionHistory && (
          <div className='activity-cont-frame-left'>
            <div className='audit-modal-cont-head'>
              <div className='audit-modal-cont-head-content'>
                <div className='audit-modal-cont-head-content-text'>
                  <h1>Version History</h1>
                  <p
                    className='version-history-subtext'
                    style={{ marginBottom: '3px' }}
                  >
                    Showing the 5 most recent screenings. Older versions
                    aren&apos;t retained.
                  </p>
                </div>
              </div>
            </div>
            <div className='view-history-head-frame'>
              <div className='view-history-head-frame-cell'>
                <div className='view-history-head-frame-cell-cont'>
                  <div className='view-history-head-frame-cell-cont-text'>
                    Date Screened
                  </div>
                </div>
              </div>
              <div className='view-history-head-frame-cell'>
                <div className='view-history-head-frame-cell-cont'>
                  <div className='view-history-head-frame-cell-cont-text'>
                    Total Results
                  </div>
                </div>
              </div>
              <div className='view-history-head-frame-cell'>
                <div className='view-history-head-frame-cell-cont'>
                  <div className='view-history-head-frame-cell-cont-text'>
                    Results Added
                  </div>
                </div>
              </div>
              <div className='view-history-head-frame-cell-empty'></div>
            </div>
            <div
              className='view-history-body-frame'
              style={{ height: 'auto', maxHeight: '320px' }}
            >
              {verisonsArr.map((ver: any, index: number) => (
                <React.Fragment key={ver._id}>
                  <ViewHistoryRow
                    version={ver}
                    versionIndex={index}
                    isSelected={selectedVersionIndex === index}
                    handleVersionSelect={(idx: number) => {
                      setSelectedVersionIndex(idx);
                      handleTabSwitch('Results');
                    }}
                  />
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
        <div className='activity-cont-frame-right'>
          <div
            className='activity-cont-frame-left-table'
            // Side by side with Audit Log (no Version History card to match),
            // this keeps the CSS default 470px min-height instead of shrinking
            // to content, so a single shared user doesn't leave a short card
            // next to a tall one.
            style={
              showVersionHistory
                ? { minHeight: 'auto', height: 'auto' }
                : undefined
            }
          >
            <div className='activity-cont-frame-left-table-cont'>
              <div className='activity-cont-frame-left-table-cont-text-sup'>
                <div className='activity-cont-frame-left-table-cont-text-sup-bagde'>
                  <div className='activity-cont-frame-left-table-cont-text-sup-bagde-frame'>
                    <div>
                      Shared with{' '}
                      <div
                        className='activity-cont-frame-left-table-cont-text-sup-bagde-btn'
                        onClick={() => setShareModal(true)}
                      >
                        + Share
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              className='activity-cont-frame-left-table-feed'
              style={{ paddingTop: '15px', gap: 'var(--spacing-lg, 15px)' }}
            >
              <ShareWithRow member={owner} perms={'Owner'} />
              <div className='activity-cont-frame-left-table-feed-row-cont-divider'></div>
              {sharedWithUsers.map((member: string, i: number) => (
                <>
                  <ShareWithRow key={i} member={member} perms={'Contributer'} />
                  <div className='activity-cont-frame-left-table-feed-row-cont-divider'></div>
                </>
              ))}
            </div>
            <div className='activity-cont-frame-pagination'>
              <div className='activity-cont-frame-pagination-text'>
                Page {currentPageSharedWith} of {totalPagesSharedWith}
              </div>
              <div className='activity-cont-frame-pagination-action'>
                <div className='activity-cont-frame-pagination-action-prev'>
                  <div
                    className='activity-cont-frame-pagination-action-text'
                    onClick={handlePrevPageSharedWith}
                  >
                    Previous
                  </div>
                </div>
                <div className='activity-cont-frame-pagination-action-prev'>
                  <div
                    className='activity-cont-frame-pagination-action-text'
                    onClick={handleNextPageSharedWith}
                  >
                    Next
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {!showVersionHistory && (
          <div className='activity-cont-frame-right'>{auditLogCard}</div>
        )}
      </div>

      {showVersionHistory && (
        <div
          className='activity-cont-frame-right'
          style={{ paddingTop: '20px', marginBottom: '30px' }}
        >
          {auditLogCard}
        </div>
      )}
    </>
  );
};

export default ActivityComponent;
